'use client';
import React, { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { LearnDataVideo } from '@osmosis/shared/types/learn';

import LearnSidenav from '../sidenav/LearnSidenav';
import { usePagesContext } from '../../../context/PagesProvider';
import { getEmbedSidebar } from './EmbedSidebar';

type Props = PropsWithChildren<{
  name: LearnDataVideo['name'];
  sidebar?: LearnDataVideo['sidebar'];
  isLoggedInView?: boolean;
}>;

/**
 * Lays out the embed. When the embed was opened with a playlist (either a
 * `concepts` list or `from=/playlist/...`) the playlist is rendered beside it.
 */
const EmbedPlaylistLayout = ({
  name,
  sidebar,
  isLoggedInView,
  children,
}: Props) => {
  const { query } = usePagesContext();
  const intl = useIntl();

  const playlistTitle = intl.formatMessage({
    id: 'learn.embed.playlist',
    defaultMessage: 'Playlist',
  });

  const embedSidebar = getEmbedSidebar({ query, sidebar, playlistTitle });

  if (!embedSidebar) {
    return (
      <Box
        className="mx-auto
    w-screen
    h-[56.25vw]
    max-h-screen
    overflow-hidden"
      >
        {children}
      </Box>
    );
  }

  return (
    <Flex
      data-testid="embed-playlist-layout"
      className="w-screen h-screen overflow-hidden flex-col md:flex-row"
    >
      {/* h-full, not an aspect ratio: the row stretches its children, so the
          player owns the height and it stays put while videos swap */}
      <Box className="w-full md:w-[70%] h-full overflow-hidden">{children}</Box>
      <Box
        data-testid="embed-playlist-sidebar"
        className="w-full md:w-[30%] h-full overflow-y-auto border-l-1 border-l-gray-200 dark:border-l-gray-700 py-2"
      >
        <LearnSidenav
          name={name}
          sidebar={embedSidebar}
          isLoggedInView={isLoggedInView}
          hideHeader
        />
      </Box>
    </Flex>
  );
};

export default EmbedPlaylistLayout;
